import { useEffect, useState } from "react";
import {
  FiSearch,
  FiDownload,
  FiTrendingUp,
  FiActivity,
  FiUserPlus,
  FiEye,
} from "react-icons/fi";
import { IoChevronDown } from "react-icons/io5";
import { LuUsers } from "react-icons/lu";
import { capitalize } from "../../utils/helpers";
import { useNavigate } from "react-router-dom";
import api from "../../configs/api";
import { MapPin } from "lucide-react";

const fallback_placeholder =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='150' viewBox='0 0 200 150'%3E%3Crect fill='%23e5e7eb' width='200' height='150'/%3E%3Cpath fill='%239ca3af' d='M100 40l60 40H40z'/%3E%3Crect fill='%236b7280' x='80' y='80' width='40' height='40'/%3E%3C/svg%3E";

const getDaysDifference = (date) => {
  const now = new Date();
  const createdDate = new Date(date);
  const diffTime = Math.abs(now - createdDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

const months = [
  { label: "All Months", value: "All" },
  { label: "January", value: "January" },
  { label: "February", value: "February" },
  { label: "March", value: "March" },
  { label: "April", value: "April" },
  { label: "May", value: "May" },
  { label: "June", value: "June" },
  { label: "July", value: "July" },
  { label: "August", value: "August" },
  { label: "September", value: "September" },
  { label: "October", value: "October" },
  { label: "November", value: "November" },
  { label: "December", value: "December" },
];

const StatCard = ({ title, value, badge, link, icon: Icon, iconBg }) => (
  <div className="bg-background-card rounded-lg p-4 sm:p-5 shadow-sm h-full">
    <div className="flex justify-between items-start mb-4">
      <div>
        <p className="text-text-primary font-medium text-sm mb-2">{title}</p>
        <h3 className="text-2xl font-semibold text-text-primary flex gap-1 items-center">
          {badge}
          {value}
        </h3>
      </div>
      <div
        className="w-12 h-12 rounded-full flex items-center justify-center"
        style={{ backgroundColor: iconBg }}
      >
        <Icon className="text-white text-xl size-7" />
      </div>
    </div>
    <div className="flex justify-between items-center text-text-muted leading-relaxed underline underline-offset-2  text-xs">
      {link}{" "}
    </div>
  </div>
);

const StatCardSkeleton = () => (
  <div className="bg-background-card rounded-lg p-5 shadow-sm animate-pulse h-full">
    <div className="h-5 bg-gray-200 rounded w-1/3 mb-4" />
    <div className="h-8 bg-gray-300 rounded w-1/2 mb-6" />
    <div className="h-4 bg-gray-200 rounded w-1/4" />
  </div>
);

const InsightCardSkeleton = () => (
  <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-4 animate-pulse">
    <div className="flex flex-col lg:flex-row gap-4">
      <div className="flex-shrink-0">
        <div className="w-24 h-16 bg-gray-200 rounded-md" />
      </div>
      <div className="flex-1 space-y-3">
        <div className="h-4 bg-gray-200 rounded w-3/4" />
        <div className="h-3 bg-gray-200 rounded w-1/2" />
        <div className="h-3 bg-gray-200 rounded w-2/3" />
      </div>
    </div>
  </div>
);

const NoInsightsScreen = ({ isFiltered, onReset }) => (
  <div className="bg-white rounded-lg p-8 text-center justify-center shadow-sm max-w-md mx-auto">
    <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
      <FiTrendingUp className="w-10 h-10 text-primary" />
    </div>
    <h3 className="text-xl font-semibold text-text-primary mb-2">
      {isFiltered ? "No Results Found" : "No Insights Available"}
    </h3>
    <p className="text-text-muted mb-6">
      {isFiltered
        ? "Try adjusting your filters or search query to find what you're looking for."
        : "Market insights will appear here once data is available. Check back later."}
    </p>
    {isFiltered && (
      <button
        onClick={onReset}
        className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-opacity-90 transition-colors font-medium"
      >
        Clear All Filters
      </button>
    )}
  </div>
);

const SearchFilterBar = ({
  searchTerm,
  setSearchTerm,
  selectedMonth,
  setSelectedMonth,
  onExport,
}) => {
  return (
    <div className="sticky my-3 top-0 bg-background z-10 mb-2">
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <div className="flex-1 relative min-w-0">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            placeholder="Search insights, location, metric..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary"
          />
        </div>

        <div className="flex flex-wrap gap-3 sm:flex-nowrap">
          <div className="relative flex-1 sm:flex-initial">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="appearance-none w-full pl-4 pr-10 py-2.5 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary cursor-pointer sm:min-w-[140px]"
            >
              {months.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
            <IoChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
          </div>

          <button
            onClick={onExport}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            <FiDownload className="text-text-secondary" />
            <span className="text-sm font-medium text-text-secondary">
              Export
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

const FilterChipsBar = ({
  selectedMonth,
  setSelectedMonth,
  searchTerm,
  setSearchTerm,
}) => {
  if (!searchTerm || !selectedMonth) return;
  const isFiltering =
    (searchTerm && searchTerm.trim() !== "") || selectedMonth !== "All";

  const handleClearAll = () => {
    setSearchTerm("");
    setSelectedMonth("All");
  };

  return (
    <div className="py-3 flex flex-wrap gap-2 items-center">
      {searchTerm.trim() !== "" && (
        <div className="flex items-center bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium gap-2">
          <span>Search: {searchTerm}</span>
          <span
            onClick={() => setSearchTerm("")}
            className="cursor-pointer hover:text-red-500"
          >
            ✕
          </span>
        </div>
      )}

      {selectedMonth !== "All" && (
        <div className="flex items-center bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium gap-2">
          <span>Month: {selectedMonth}</span>
          <span
            onClick={() => setSelectedMonth("All")}
            className="cursor-pointer hover:text-red-500"
          >
            ✕
          </span>
        </div>
      )}

      {isFiltering && (
        <button
          onClick={handleClearAll}
          className="ml-1 px-4 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-full text-sm font-medium transition"
        >
          Clear All
        </button>
      )}
    </div>
  );
};

const InsightCard = ({ insight }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/admin/market-insights/${insight?.propertyId}`);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  return (
    <div
      onClick={handleClick}
      className="bg-white rounded-lg cursor-pointer font-poppins shadow-sm border-2 border-gray-200 hover:shadow-md transition-shadow"
    >
      <div className="flex flex-col lg:flex-row gap-4 p-1">
        <div className="flex flex-col md:flex-row gap-3 flex-1">
          <div className="flex-shrink-0 rounded-md overflow-hidden flex flex-col items-center">
            <img
              src={insight?.thumbnail ?? fallback_placeholder}
              alt={insight.title}
              className="w-20 h-full sm:w-24 sm:aspect-square object-cover"
            />
          </div>

          <div className="px-2 sm:px-4 lg:px-0 flex flex-1 flex-col h-full justify-between py-2">
            <div>
              <h2 className="font-medium text-base sm:text-sm mb-1 line-clamp-1">
                {insight.title}
              </h2>
              <p className="text-[0.7rem] line-clamp-1 text-text-muted flex items-center gap-1">
                <MapPin size={13} />
                {insight?.location}
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 mt-2 pr-4">
              <div className="flex flex-row gap-4">
                <div className="flex items-center gap-1 text-text-secondary text-xs font-medium">
                  <LuUsers className="w-4 h-4" />
                  <span>{insight.views} views</span>
                </div>
              </div>
              <div className="text-xs text-text-muted font-medium flex">
                <p className=" mr-1">Type: </p>
                <p className=" whitespace-nowrap">{capitalize(insight.type)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const MarketInsights = () => {
  const [insights, setInsights] = useState([]);
  const [filteredInsights, setFilteredInsights] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("All");
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [loading, setLoading] = useState(true);
  const [statsData, setStatsData] = useState([]);

  useEffect(() => {
    const fetchPropertyPerformance = async () => {
      try {
        setLoading(true);
        const response = await api.get("/analytics/properties/performance");

        if (response.data.success) {
          const totals = response.data;

          setInsights(totals.data);
          setFilteredInsights(totals.data);

          setStatsData([
            {
              id: 1,
              title: "Total Properties",
              badge: <FiActivity size={22} />,
              value: totals.totalProperties?.toString(),
              link: "Total uploaded properties",
              icon: FiTrendingUp,
              iconBg: "#4F46E5",
            },
            {
              id: 2,
              title: "Total Impressions",
              badge: <FiActivity size={22} />,
              value: totals.totalImpressions?.toString(),
              link: "How many people saw your properties",
              icon: FiTrendingUp,
              iconBg: "#6C63FF",
            },
            {
              id: 3,
              title: "Total Views",
              badge: <FiEye size={22} />,
              value: totals.totalViews?.toString(),
              link: "Times users opened your property pages",
              icon: FiEye,
              iconBg: "#0CC763",
            },
            {
              id: 4,
              title: "Total Leads",
              badge: <FiUserPlus size={22} />,
              value: totals.totalLeads?.toString(),
              link: "Direct contacts initiated by users",
              icon: LuUsers,
              iconBg: "#0C9CFC",
            },
          ]);
        }
      } catch (error) {
        console.error("Error fetching property performance:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPropertyPerformance();
  }, []);

  useEffect(() => {
    let filtered = [...insights];

    if (searchTerm.trim() !== "") {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter((insight) => {
        return (
          insight.title?.toLowerCase().includes(lowerSearch) ||
          insight.location?.toLowerCase().includes(lowerSearch) ||
          insight.newViews?.toLowerCase().includes(lowerSearch) ||
          insight.category?.toLowerCase().includes(lowerSearch)
        );
      });
    }

    if (selectedMonth !== "All") {
      filtered = filtered.filter((insight) => {
        if (!insight.createdAt) return false;
        const insightMonth = new Date(insight.createdAt).toLocaleString(
          "default",
          { month: "long" }
        );
        return insightMonth === selectedMonth;
      });
    }

    setFilteredInsights(filtered);
  }, [searchTerm, selectedMonth, selectedFilter, insights]);

  const handleExport = () => {
    if (!filteredInsights || filteredInsights.length === 0) return;
    const headers = [
      "ID",
      "Title",
      "Category",
      "Location",
      "Metric",
      "Status",
      "Days Old",
      "Created",
    ];
    const rows = filteredInsights.map((r) => {
      const days = getDaysDifference(r.createdAt);
      return [
        r.id,
        r.title,
        r.category,
        r.location,
        r.newViews,
        days,
        new Date(r.createdAt).toLocaleDateString(),
      ]
        .map((cell) => `"${String(cell || "").replace(/"/g, '""')}"`)
        .join(",");
    });
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `market_insights_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();  
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen font-quicksand bg-background py-3 sm:py-4 md:py-6 pl-3 sm:pl-4 md:pl-6 pr-2 sm:pr-3 md:pr-4">
      <div className="mb-6 font-poppins">
        <h1 className="text-2xl font-semibold text-text-primary mb-1">
          Market Insights
        </h1>
        <p className="text-text-muted text-sm">
          Data-driven insights and recommendations
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {loading
          ? Array.from({ length: 4 }).map((_, i) => (
              <StatCardSkeleton key={i} />
            ))
          : statsData.map((stat) => <StatCard key={stat.id} {...stat} />)}
      </div>

      <SearchFilterBar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedMonth={selectedMonth}
        setSelectedMonth={setSelectedMonth}
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
        onExport={handleExport}
      />

      <FilterChipsBar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedMonth={selectedMonth}
        setSelectedMonth={setSelectedMonth}
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
      />

      <div className="mt-6">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <InsightCardSkeleton key={i} />
            ))}
          </div>
        ) : filteredInsights.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredInsights.map((insight, i) => (
              <InsightCard key={i} insight={insight} />
            ))}
          </div>
        ) : (
          <div className="flex justify-center items-center min-h-[50vh]">
            <NoInsightsScreen
              isFiltered={searchTerm || selectedMonth !== "All"}
              onReset={() => {
                setSearchTerm("");
                setSelectedMonth("All");
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketInsights;

// import { useEffect, useState } from "react";
// import {
//   FiSearch,
//   FiDownload,
//   FiFilter,
//   FiTrendingUp,
//   FiClock,
//   FiActivity,
//   FiUserPlus,
//   FiEye,
// } from "react-icons/fi";
// import { IoChevronDown, IoTrendingUp, IoWalletOutline } from "react-icons/io5";
// import { LuUsers } from "react-icons/lu";
// import { MdNewReleases, MdHistory, MdArchive } from "react-icons/md";
// import { timeAgo } from "../../utils/helpers";
// import { LuIndianRupee } from "react-icons/lu";
// import { useNavigate } from "react-router-dom";
// import api from "../../configs/api";

// const getDaysDifference = (date) => {
//   const now = new Date();
//   const createdDate = new Date(date);
//   const diffTime = Math.abs(now - createdDate);
//   const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
//   return diffDays;
// };

// const getStatusFromDays = (date) => {
//   const days = getDaysDifference(date);
//   if (days <= 7) return "new";
//   if (days <= 30) return "old";
//   return "older";
// };

// const default_property =
//   "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='150' viewBox='0 0 200 150'%3E%3Crect fill='%23e5e7eb' width='200' height='150'/%3E%3Cpath fill='%239ca3af' d='M100 40l60 40H40z'/%3E%3Crect fill='%236b7280' x='80' y='80' width='40' height='40'/%3E%3C/svg%3E";

// const months = [
//   { label: "All Months", value: "All" },
//   { label: "January", value: "January" },
//   { label: "February", value: "February" },
//   { label: "March", value: "March" },
//   { label: "April", value: "April" },
//   { label: "May", value: "May" },
//   { label: "June", value: "June" },
//   { label: "July", value: "July" },
//   { label: "August", value: "August" },
//   { label: "September", value: "September" },
//   { label: "October", value: "October" },
//   { label: "November", value: "November" },
//   { label: "December", value: "December" },
// ];

// // Status
// const statusOptions = [
//   { label: "All Status", value: "All" },
//   { label: "New (1-7 days)", value: "new" },
//   { label: "Old (8-30 days)", value: "old" },
//   { label: "Older (30+ days)", value: "older" },
// ];

// // stats card
// const StatCard = ({
//   title,
//   value,
//   badge,
//   link,
//   percentage,
//   isPositive,
//   icon: Icon,
//   iconBg,
// }) => (
//   <div className="bg-background-card rounded-lg p-4 sm:p-5 shadow-sm">
//     <div className="flex justify-between items-start mb-4">
//       <div>
//         <p className="text-text-primary font-medium text-sm mb-2">{title}</p>
//         <h3 className="text-2xl font-semibold text-text-primary flex gap-1 items-center">
//           {badge}
//           {value}
//         </h3>
//       </div>
//       <div
//         className="w-12 h-12 rounded-full flex items-center justify-center"
//         style={{ backgroundColor: iconBg }}
//       >
//         <Icon className="text-white text-xl size-7" />
//       </div>
//     </div>
//     <div className="flex justify-between items-center">
//       <button className="text-text-muted leading-relaxed underline underline-offset-2 cursor-pointer text-sm hover:text-primary">
//         {link}
//       </button>
//       <span
//         className={`text-sm font-semibold ${
//           isPositive ? "text-status-success" : "text-status-danger"
//         }`}
//       >
//         {percentage}
//       </span>
//     </div>
//   </div>
// );

// const InsightCardSkeleton = () => (
//   <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-4 animate-pulse">
//     <div className="flex flex-col lg:flex-row gap-4">
//       <div className="flex-shrink-0">
//         <div className="w-24 h-16 bg-gray-200 rounded-md" />
//       </div>
//       <div className="flex-1 space-y-3">
//         <div className="h-4 bg-gray-200 rounded w-3/4" />
//         <div className="h-3 bg-gray-200 rounded w-1/2" />
//         <div className="h-3 bg-gray-200 rounded w-2/3" />
//       </div>
//     </div>
//   </div>
// );

// const NoInsightsScreen = ({ isFiltered, onReset }) => (
//   <div className="bg-white rounded-lg p-8 text-center justify-center shadow-sm">
//     <div className="max-w-full mx-auto">
//       <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
//         <FiTrendingUp className="w-10 h-10 text-primary" />
//       </div>
//       <h3 className="text-xl font-semibold text-text-primary mb-2">
//         {isFiltered ? "No Results Found" : "No Insights Available"}
//       </h3>
//       <p className="text-text-muted mb-6">
//         {isFiltered
//           ? "Try adjusting your filters or search query to find what you're looking for."
//           : "Market insights will appear here once data is available. Check back later."}
//       </p>
//       {isFiltered && (
//         <button
//           onClick={onReset}
//           className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-opacity-90 transition-colors font-medium"
//         >
//           Clear All Filters
//         </button>
//       )}
//     </div>
//   </div>
// );

// // search filter
// const SearchFilterBar = ({
//   searchTerm,
//   setSearchTerm,
//   selectedMonth,
//   setSelectedMonth,
//   selectedFilter,
//   setSelectedFilter,
//   onExport,
// }) => {
//   return (
//     <div className="sticky top-0 py-4 bg-background shadow-sm z-10">
//       <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
//         <div className="flex-1 relative min-w-0">
//           <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
//           <input
//             type="text"
//             placeholder="Search insights, location, metric..."
//             value={searchTerm}
//             onChange={(e) => setSearchTerm(e.target.value)}
//             className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary"
//           />
//         </div>

//         <div className="flex flex-wrap gap-3 sm:flex-nowrap">
//           <div className="relative flex-1 sm:flex-initial">
//             <select
//               value={selectedMonth}
//               onChange={(e) => setSelectedMonth(e.target.value)}
//               className="appearance-none w-full pl-4 pr-10 py-2.5 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary cursor-pointer sm:min-w-[140px]"
//             >
//               {months.map((month) => (
//                 <option key={month.value} value={month.value}>
//                   {month.label}
//                 </option>
//               ))}
//             </select>
//             <IoChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
//           </div>

//           <div className="relative flex-1 sm:flex-initial">
//             <FiFilter className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
//             <select
//               value={selectedFilter}
//               onChange={(e) => setSelectedFilter(e.target.value)}
//               className="appearance-none w-full pl-9 pr-10 py-2.5 border border-gray-300 rounded-lg bg-white text-sm focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary cursor-pointer sm:min-w-[180px]"
//             >
//               {statusOptions.map((filter) => (
//                 <option key={filter.value} value={filter.value}>
//                   {filter.label}
//                 </option>
//               ))}
//             </select>
//             <IoChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
//           </div>

//           <button
//             onClick={onExport}
//             className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
//           >
//             <FiDownload className="text-text-secondary" />
//             <span className="text-sm font-medium text-text-secondary">
//               Export
//             </span>
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// const FilterChipsBar = ({
//   selectedMonth,
//   setSelectedMonth,
//   selectedFilter,
//   setSelectedFilter,
//   searchTerm,
//   setSearchTerm,
// }) => {
//   const getStatusLabel = (value) => {
//     const option = statusOptions.find((opt) => opt.value === value);
//     return option ? option.label : value;
//   };
// };

// const InsightCard = ({ insight }) => {
//   const navigate = useNavigate();

//   const handleClick = () => {
//     navigate(`/admin/market-insights/${insight.id}`);
//     setTimeout(() => {
//       window.scrollTo(0, 0);
//     }, 0);
//   };

//   return (
//     <div
//       onClick={handleClick}
//       className="bg-white rounded-lg cursor-pointer font-poppins shadow-sm border-2 border-gray-200 hover:shadow-md transition-shadow"
//     >
//       <div className="flex flex-col lg:flex-row gap-4 p-1">
//         <div className="flex flex-col md:flex-row gap-3 flex-1">
//           <div className="flex-shrink-0  rounded-md overflow-hidden flex flex-col items-center">
//             <img
//               src={insight.image || default_property}
//               alt={insight.title}
//               className="w-20 h-full sm:w-24 sm:aspect-square object-cover"
//             />
//           </div>

//           {/* Details */}
//           <div className="px-2 sm:px-4 lg:px-0 flex flex-1 flex-col h-full justify-between py-2">
//             <h2 className="font-medium text-base sm:text-sm mb-2 line-clamp-1">
//               {insight.title}
//             </h2>

//             {/* Status + Views */}
//             <div className="flex flex-wrap items-center justify-between gap-3 mt-2 pr-4">
//               <div className="flex flex-row gap-4">
//                 {/* Views */}
//                 <div className="flex items-center gap-1 text-text-secondary text-xs font-medium">
//                   <LuUsers className="w-4 h-4" />
//                   <span>{insight.newViews} views</span>
//                 </div>
//               </div>
//               <div className="text-xs flex">
//                 <p className="text-text-muted font-medium mr-1">Type: </p>
//                 <p className="text-text-primary font-medium whitespace-nowrap">
//                   {insight.category}
//                 </p>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// const MarketInsights = () => {
//   const [insights, setInsights] = useState([]);
//   const [filteredInsights, setFilteredInsights] = useState([]);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [selectedMonth, setSelectedMonth] = useState("All");
//   const [selectedFilter, setSelectedFilter] = useState("All");
//   const [loading, setLoading] = useState(true);
//   const [statsData, setStatsData] = useState([]);

//   useEffect(() => {
//     const fetchPropertyPerformance = async () => {
//       try {
//         setLoading(true);
//         const response = await api.get("/analytics/properties/performance");

//         if (response.data.success) {
//           const apiData = response.data.data;

//           const transformedInsights = apiData.map((property) => ({
//             id: property.propertyId,
//             title: property.title,
//             createdAt: new Date().toISOString(), // Using current date as API doesn't provide creation date
//             image: property.thumbnail || default_property,
//             newViews: property.views.toString(),
//             category:
//               property.type.charAt(0).toUpperCase() + property.type.slice(1),
//             location: property.location,
//             impressions: property.impressions,
//             leads: property.leads,
//           }));

//           setInsights(transformedInsights);
//           setFilteredInsights(transformedInsights);

//           const totalViews = apiData.reduce((sum, p) => sum + p.views, 0);
//           const totalImpressions = apiData.reduce(
//             (sum, p) => sum + p.impressions,
//             0
//           );
//           const totalLeads = apiData.reduce((sum, p) => sum + p.leads, 0);

//           setStatsData([
//             {
//               id: 1,
//               title: "Total Impressions",
//               badge: <FiActivity size={22} />,
//               value: totalImpressions.toString(),
//               link: "How many people saw your properties",
//               percentage: "—",
//               isPositive: true,
//               icon: FiTrendingUp,
//               iconBg: "#6C63FF",
//             },
//             {
//               id: 2,
//               title: "Total Views",
//               badge: <FiEye size={22} />,
//               value: totalViews.toString(),
//               link: "Times users opened your property pages",
//               percentage: "—",
//               isPositive: true,
//               icon: FiEye,
//               iconBg: "#0CC763",
//             },
//             {
//               id: 3,
//               title: "Total Leads",
//               badge: <FiUserPlus size={22} />,
//               value: totalLeads.toString(),
//               link: "Direct contacts initiated by users",
//               percentage: "—",
//               isPositive: true,
//               icon: LuUsers,
//               iconBg: "#0C9CFC",
//             },
//           ]);
//         }
//       } catch (error) {
//         console.error("Error fetching property performance:", error);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchPropertyPerformance();
//   }, []);

//   useEffect(() => {
//     let filtered = [...insights];

//     // Search filter
//     if (searchTerm.trim() !== "") {
//       const lowerSearch = searchTerm.toLowerCase();
//       filtered = filtered.filter((insight) => {
//         return (
//           insight.title?.toLowerCase().includes(lowerSearch) ||
//           insight.location?.toLowerCase().includes(lowerSearch) ||
//           insight.newViews?.toLowerCase().includes(lowerSearch) ||
//           insight.category?.toLowerCase().includes(lowerSearch)
//         );
//       });
//     }

//     // Month filter
//     if (selectedMonth !== "All") {
//       filtered = filtered.filter((insight) => {
//         if (!insight.createdAt) return false;
//         const insightMonth = new Date(insight.createdAt).toLocaleString(
//           "default",
//           { month: "long" }
//         );
//         return insightMonth === selectedMonth;
//       });
//     }

//     // Status filter based on days
//     if (selectedFilter !== "All") {
//       filtered = filtered.filter((insight) => {
//         const status = getStatusFromDays(insight.createdAt);
//         return status === selectedFilter;
//       });
//     }

//     setFilteredInsights(filtered);
//   }, [searchTerm, selectedMonth, selectedFilter, insights]);

//   const handleExport = () => {
//     if (!filteredInsights || filteredInsights.length === 0) return;
//     const headers = [
//       "ID",
//       "Title",
//       "Category",
//       "Location",
//       "Metric",
//       "Status",
//       "Days Old",
//       "Created",
//     ];
//     const rows = filteredInsights.map((r) => {
//       const status = getStatusFromDays(r.createdAt);
//       const days = getDaysDifference(r.createdAt);
//       return [
//         r.id,
//         r.title,
//         r.category,
//         r.location,
//         r.newViews,
//         status,
//         days,
//         new Date(r.createdAt).toLocaleDateString(),
//       ]
//         .map((cell) => `"${String(cell || "").replace(/"/g, '""')}"`)
//         .join(",");
//     });
//     const csv = [headers.join(","), ...rows].join("\n");
//     const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
//     const url = URL.createObjectURL(blob);
//     const a = document.createElement("a");
//     a.href = url;
//     a.download = `market_insights_${new Date().toISOString().slice(0, 10)}.csv`;
//     a.click();
//     URL.revokeObjectURL(url);
//   };

//   return (
//     <div className="min-h-screen font-quicksand bg-background py-3 sm:py-4 md:py-6 pl-3 sm:pl-4 md:pl-6 pr-2 sm:pr-3 md:pr-4">
//       <div className="mb-4 cursor-default font-poppins">
//         <h1 className="text-2xl font-semibold text-text-primary mb-1">
//           Market Insights
//         </h1>
//         <p className="text-text-muted text-sm">
//           Data-driven insights and recommendations
//         </p>
//       </div>

//       <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
//         {statsData.map((stat) => (
//           <StatCard key={stat.id} {...stat} />
//         ))}
//       </div>

//       <div>
//         <SearchFilterBar
//           searchTerm={searchTerm}
//           setSearchTerm={setSearchTerm}
//           selectedMonth={selectedMonth}
//           setSelectedMonth={setSelectedMonth}
//           selectedFilter={selectedFilter}
//           setSelectedFilter={setSelectedFilter}
//           onExport={handleExport}
//         />

//         <FilterChipsBar
//           searchTerm={searchTerm}
//           setSearchTerm={setSearchTerm}
//           selectedMonth={selectedMonth}
//           setSelectedMonth={setSelectedMonth}
//           selectedFilter={selectedFilter}
//           setSelectedFilter={setSelectedFilter}
//         />
//       </div>

//       <div>
//         <div>
//           <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 scroll-overlay">
//             {loading ? (
//               Array.from({ length: 3 }).map((_, i) => (
//                 <InsightCardSkeleton key={i} />
//               ))
//             ) : Array.isArray(filteredInsights) &&
//               filteredInsights.length > 0 ? (
//               filteredInsights.map((insight) => (
//                 <InsightCard key={insight.id} insight={insight} />
//               ))
//             ) : insights.length === 0 ? (
//               <NoInsightsScreen isFiltered={false} />
//             ) : (
//               <NoInsightsScreen
//                 isFiltered={true}
//                 onReset={() => {
//                   setSearchTerm("");
//                   setSelectedMonth("All");
//                   setSelectedFilter("All");
//                 }}
//               />
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default MarketInsights;
